import { createSelector } from '@reduxjs/toolkit';
import qs from 'querystring';

import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { selectPageType, selectPathname } from 'src/redux/navigation';
import {
  selectNextPageNumber,
  selectPreviousPageNumber,
  selectRequestOptionsAsQueryObject,
} from 'src/redux/products/selectors';
import {
  selectProductsReturnedCount,
  selectTotalPages,
  selectTotalProducts,
} from 'src/redux/selectors';
import { PageType } from 'src/types';
import { ProductPageContainerProps } from '../types';

const selectPageProp = (_: ReduxApplicationState, { page }: ProductPageContainerProps) => page;

export const selectNumberOfProductsViewed = createSelector(
  selectProductsReturnedCount,
  selectTotalProducts,
  selectTotalPages,
  selectPageProp,
  (productsReturnedCount, totalProducts, totalPages, page) =>
    page < totalPages ? page * productsReturnedCount : totalProducts,
);
const selectProducts = ({ products }: ReduxApplicationState) => products;

export const selectProductPaginationProgress = createSelector(
  selectTotalProducts,
  selectNumberOfProductsViewed,
  (totalNumberOfProducts, numberOfProductsViewed) =>
    Math.floor((100 / totalNumberOfProducts) * numberOfProductsViewed),
);

export const selectLoadMoreProductsURL = createSelector(
  selectRequestOptionsAsQueryObject,
  selectNextPageNumber,
  (selectedSortAndFilterOptionsQuery, nextPage) =>
    `?${qs.stringify({
      ...selectedSortAndFilterOptionsQuery,
      page: nextPage,
    })}`,
);

export const selectLoadPreviousProductsURL = createSelector(
  selectRequestOptionsAsQueryObject,
  selectPreviousPageNumber,
  selectPathname,
  (selectedSortAndFilterOptionsQuery, previousPage, pathname) => {
    const queryString = `${qs.stringify({
      ...selectedSortAndFilterOptionsQuery,
      ...(previousPage > 1 && { page: previousPage }),
    })}`;

    return queryString.length > 0 ? `${pathname}?${queryString}` : pathname;
  },
);

export const selectIsFirstDisplayedPage = (
  { products }: ReduxApplicationState,
  { page }: ProductPageContainerProps,
) => parseInt(Object.keys(products.items)[0]) === page;

export const selectIsLastDisplayedPage = (
  { products }: ReduxApplicationState,
  { page }: ProductPageContainerProps,
) => parseInt(Object.keys(products.items).slice(-1)[0]) === page;

export const selectOrderedProductPageNumbers = createSelector(selectProducts, (products) =>
  Object.keys(products.items)
    .map((pageNo) => parseInt(pageNo))
    .sort((a, b) => a - b),
);

const selectProductItems = ({ products: { items } }: ReduxApplicationState) => items;

export const selectNavigatedFromOrToPageOne = createSelector([selectProductItems], (items) =>
  Object.keys(items).includes('1'),
);

export const selectRedirectedFromSearch = ({
  router: {
    location: { search },
  },
}: ReduxApplicationState) => !!new URLSearchParams(search).get('categoryRedirect');

export const selectReferredBy = createSelector(selectPageType, (pageType) =>
  pageType
    ? {
        [PageType.Category]: 'plp',
        [PageType.Search]: 'search',
        [PageType.Partner]: 'search',
      }[pageType]
    : undefined,
);
